import { useEffect } from "react";
import { ErrorScreen, type ErrorKind } from "../components/design/ErrorScreen";
import { api } from "../api";
import { navigate, routes } from "../router";

const KNOWN_KINDS = new Set<ErrorKind>([
  "bot_blocked",
  "recall_down",
  "offline",
  "missing_calendar_permissions",
  "finalize_failed",
]);

/** Generic edge-state route at `/edge/<kind>`. Resolves the kind from the
 *  URL and renders <ErrorScreen> with the matching heading + description +
 *  action set. The SPA navigates here programmatically when corresponding
 *  error conditions fire (bot blocked, Recall down, offline, missing
 *  calendar perms, finalize-all-failed); the routes are also reachable
 *  directly for engineers via the dev-gated sidebar group.
 */
export function EdgeState({
  errorKind,
  meetingId,
}: {
  errorKind: string;
  meetingId: string | null;
}) {
  const kind: ErrorKind = (
    KNOWN_KINDS.has(errorKind as ErrorKind) ? errorKind : "offline"
  ) as ErrorKind;

  // Offline: auto-recover by polling navigator.onLine and navigating back.
  useEffect(() => {
    if (kind !== "offline") return;
    if (typeof window === "undefined") return;
    const check = () => {
      if (window.navigator.onLine) {
        navigate(routes.home(), /*replace*/ true);
      }
    };
    const handle = setInterval(check, 1500);
    window.addEventListener("online", check);
    return () => {
      clearInterval(handle);
      window.removeEventListener("online", check);
    };
  }, [kind]);

  switch (kind) {
    case "bot_blocked":
      return (
        <ErrorScreen
          kind="bot_blocked"
          heading="Meet rejected the bot"
          description={
            <>
              Your workspace's waiting-room admits new participants manually.
              Open the meeting and admit RecSpec, or try a different meeting.
            </>
          }
          actions={[
            {
              label: "Open Meet",
              primary: true,
              href: "https://meet.google.com/",
            },
            {
              label: "Try a different meeting",
              onClick: () => navigate(routes.meetingNew()),
            },
          ]}
        />
      );
    case "recall_down":
      return (
        <ErrorScreen
          kind="recall_down"
          heading="Recall.ai is unreachable"
          description={
            <>
              Our meeting-recording vendor is having a moment. Wait a beat and
              try again — or check their status page if it persists.
            </>
          }
          actions={[
            {
              label: "Retry now",
              primary: true,
              onClick: () => window.location.reload(),
            },
            {
              label: "Status page",
              href: "https://status.recall.ai/",
            },
          ]}
        />
      );
    case "offline":
      return (
        <ErrorScreen
          kind="offline"
          heading="You're offline"
          description={
            <>
              Reconnecting… we'll bounce you back to the meetings list as soon
              as the network is back.
            </>
          }
        />
      );
    case "missing_calendar_permissions":
      return (
        <ErrorScreen
          kind="missing_calendar_permissions"
          heading="Calendar access is missing"
          description={
            <>
              Starting a new Meet from the picker requires Google Calendar
              permission. Re-grant it and try again.
            </>
          }
          actions={[
            {
              label: "Re-grant access",
              primary: true,
              disabled: true,
              href: "/auth/google/calendar",
              title:
                "The Calendar OAuth flow ships with add-start-meeting-with-bot-trigger.",
            },
            {
              label: "Use a paste-link meeting instead",
              onClick: () => navigate(routes.meetingNew()),
            },
          ]}
        />
      );
    case "finalize_failed":
      return (
        <ErrorScreen
          kind="finalize_failed"
          heading="Finalize failed"
          description={
            <>
              Every picked expert hit an error during the author pass. Each
              expert's <code className="mono">FAILED.md</code> in the bundle
              has the details. Retry once any transient cause is cleared.
            </>
          }
          actions={[
            {
              label: "Retry finalize",
              primary: true,
              onClick: () => {
                if (!meetingId) {
                  navigate(routes.home());
                  return;
                }
                api
                  .finalizeMeeting(meetingId)
                  .then(() => navigate(routes.meeting(meetingId)))
                  .catch(() => navigate(routes.home()));
              },
              disabled: !meetingId,
              title: meetingId
                ? undefined
                : "Open from the meetings list to retry with context.",
            },
            {
              label: meetingId ? "Back to this meeting" : "Back to meetings",
              onClick: () =>
                navigate(meetingId ? routes.meeting(meetingId) : routes.home()),
            },
          ]}
        />
      );
  }
}
